<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { Dialog, Notify } from 'quasar';

import { useLanguageValidationErrors } from '@/composables';
import { useModuleBreadcrumbOptions } from '@/composables/breadcrumb-options';
import { getLanguageEnabled, getTranslation, provideEditLocale } from '@/composables/data-display-utils';
import { useRouteNumberParam } from '@/composables/use-route-param';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { Constants } from '@/constants';
import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';
import { formSaveNavigationGuard, resetFormToItem } from '@/helpers/form.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import {
  canBePreviewed,
  changedPageFields,
  effectiveTheme,
  getPageStatusLabel,
  isHomepage,
  isThemeLocked,
  layoutAfterThemeChange,
  layoutOptions,
  slugChangeNeedsWarning,
  type PageWritableFields,
} from '@/helpers/page.helper';
import { themeOption, themesOfferedToAll } from '@/helpers/theme.helper';
import { languageEnabledArray, localizableString, optionalSlug, slug } from '@/helpers/validation.helper';
import type { Locales, PageDto } from '@/services/types/api.types';
import { useCommunityStore, useConfigurationStore, usePagesStore } from '@/stores';
import type { BlockParent, BlockRouteFor } from '@/types/modules';

import DynamicTabs from '@/components/DynamicTabs.vue';
import FormActions from '@/components/FormActions.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import ModuleList from '@/components/ModuleList.vue';
import StandardInput from '@/components/StandardInput.vue';
import TopPageNavigation from '@/components/TopPageNavigation.vue';

const { t } = useI18n();

// STORE
const store = usePagesStore();
const configurationStore = useConfigurationStore();
const { community } = storeToRefs(useCommunityStore());

// REF
const languageEnabled = ref<Locales[]>([]);
// The layout a save sends. Kept apart from the form: vee-validate drops a field's value when the field unmounts, which
// the layout field does when the new theme offers no layout, and the API would then check the stored layout against
// the new theme.
const layoutKey = ref<string | null>(null);

// CONST
const pageId = useRouteNumberParam('id');
const blockParent = computed<BlockParent>(() => ({ type: Constants.Block.ParentType.Page, id: pageId.value }));
const blockRouteFor: BlockRouteFor = (blockId) => ({
  name: 'page_block_details',
  params: { pageId: pageId.value, moduleId: blockId },
});

const homepageId = computed(() => community.value?.options?.pages?.homepage_id ?? null);
const themes = computed(() => configurationStore.getThemes('page'));

// FORM
// A page with no slug, such as a migrated homepage, may keep none: the homepage is served at `/` whatever its slug.
const hasSavedSlug = ref(true);
const validationSchema = computed(() =>
  yup.object({
    title: localizableString(languageEnabled.value),
    slug: hasSavedSlug.value ? slug() : optionalSlug(),
    status: yup.number().required(),
    theme_key: yup.string().nullable(),
    layout_key: yup.string().nullable(),
    language_enabled: languageEnabledArray(community.value?.language_enabled),
  }),
);
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, values, resetForm, setFieldValue } = formContext;
const page = useWatchItemLoading<PageDto>(pageId, store, (item) => loadForm(item));

function loadForm(item: PageDto) {
  languageEnabled.value = getLanguageEnabled(item);
  hasSavedSlug.value = !!item.slug;
  layoutKey.value = item.layout_key;
  resetFormToItem(formContext, cloneDeep(item));
}

const { moduleOptions } = useModuleBreadcrumbOptions(blockParent, blockRouteFor);
const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(languageEnabled);

// LIFECYCLE
watch(
  pageId,
  async (id) => {
    if (id < 0) return;
    await store.fetchById(id);
  },
  { immediate: true },
);

// COMPUTED
const statusOptions = computed(() =>
  [Constants.Page.Status.Draft, Constants.Page.Status.Published].map((status) => ({
    label: getPageStatusLabel(status),
    value: status,
  })),
);

// What a save would send, which is also what the tabs mark as unsaved.
const formValues = computed(
  (): PageWritableFields => ({ ...(values as PageWritableFields), layout_key: layoutKey.value }),
);
const changed = computed(() => (page.value ? changedPageFields(page.value, formValues.value) : {}));
// Save, Cancel and the leave guard follow what a save would send, as the tabs do: vee-validate's own dirty state takes
// the layout as saved again when its field mounts anew, after a theme that offers no layout hid it.
const saveMeta = computed(() => ({ ...meta.value, dirty: Object.keys(changed.value).length > 0 }));
onBeforeRouteLeave(formSaveNavigationGuard(saveMeta));
const isInfosDirty = computed(() => ['title', 'slug', 'status'].some((field) => field in changed.value));
const isOptionsDirty = computed(() =>
  ['theme_key', 'layout_key', 'language_enabled'].some((field) => field in changed.value),
);

const tabs = computed(() => [
  { id: 'infos', name: t('page.tabs.infos'), isDirty: isInfosDirty.value, icon: 'o_info' },
  { id: 'blocks', name: t('page.tabs.blocks'), icon: 'o_widgets' },
  { id: 'options', name: t('page.tabs.options'), isDirty: isOptionsDirty.value, icon: 'more_vert' },
]);

const openedTab = computed(() => (window.location.hash ? window.location.hash.substring(1) : 'infos'));

const breadcrumbs = computed(() => {
  const items: BreadcrumbElement[] = [
    { label: page.value?.title, to: { name: 'page_details', params: { id: page.value?.id } }, icon: 'o_article' },
  ];
  if (page.value?.id) {
    items.push({ label: t('breadcrumbs.modules'), to: undefined, icon: 'o_folder', options: moduleOptions.value });
  }
  return items;
});

// A theme's name, or its key when the manifest no longer knows it.
const themeLabel = (key: string | null | undefined) => {
  const theme = themes.value.find((entry) => entry.key === key);
  return theme ? themeOption(theme, languageEnabled.value).label : (key ?? '');
};

// A page that stores a custom theme keeps it: the admin could not set it back once changed.
const isThemeFieldLocked = computed(() => isThemeLocked(themes.value, page.value?.theme_key));
const lockedThemeLabel = computed(() => themeLabel(page.value?.theme_key));
// The hint names the theme an empty field gives the page.
const themeHint = computed(() => {
  const theme = themeLabel(community.value?.default_page_theme_key);
  return values.theme_key ? t('page.theme.default_applied', { theme }) : t('page.theme.community', { theme });
});
const themeOptions = computed(() =>
  themesOfferedToAll(themes.value).map((theme) => themeOption(theme, languageEnabled.value)),
);

const currentTheme = computed(() =>
  effectiveTheme(themes.value, values.theme_key as string | null, community.value?.default_page_theme_key),
);
// A stored layout the theme no longer offers stays, shown as such, until it is changed: the site draws the default.
const layoutChoices = computed(() => {
  const options = layoutOptions(currentTheme.value, languageEnabled.value);
  if (options.length && layoutKey.value !== null && !options.some((option) => option.value === layoutKey.value)) {
    options.push({ label: t('page.layout.not-offered', { key: layoutKey.value }), value: layoutKey.value });
  }
  return options;
});

const previewDisabledReason = computed(() =>
  page.value ? canBePreviewed(page.value, homepageId.value).reason : undefined,
);

// FUNCTIONS
// The administrator picked a theme: the layout follows, or falls back to the new theme's default, which they are told.
const onThemePicked = (themeKey: string | null) => {
  const theme = effectiveTheme(themes.value, themeKey, community.value?.default_page_theme_key);
  const kept = layoutAfterThemeChange(theme, layoutKey.value);
  if (kept !== layoutKey.value) {
    Notify.create({ type: 'warning', message: t('page.layout.reset'), position: 'top', timeout: 5000 });
  }
  layoutKey.value = kept;
  setFieldValue('layout_key', kept);
};

const onLayoutPicked = (key: string | null) => {
  layoutKey.value = key;
};

const save = () => {
  const saved = page.value!;
  wrapSavingOperation(
    async () => await store.save(saved, formValues.value),
    () => {
      const updated = store.getItem(saved.id);
      if (!updated) return;
      page.value = cloneDeep(updated);
      loadForm(page.value);
    },
  );
};

// A published page's address may already be shared: changing it asks first, naming the address that stops working.
const onSubmit = handleSubmit(() => {
  const saved = page.value;
  if (!saved) return;
  if (!slugChangeNeedsWarning(saved, formValues.value)) {
    save();
    return;
  }
  const old = `/${saved.slug}`;
  Dialog.create({
    title: t('page.slug-change.title'),
    message: isHomepage(saved, homepageId.value)
      ? t('page.slug-change.message-homepage', { old })
      : t('page.slug-change.message', { old }),
    ok: { label: t('global.save'), color: 'primary', unelevated: true },
    cancel: { label: t('global.cancel'), color: 'primary', flat: true },
  }).onOk(save);
});

const onCancel = () => {
  if (page.value) loadForm(page.value);
};

const preview = () => {
  const url = store.previewURL(pageId.value);
  if (url) window.open(url, '_blank')?.focus();
};
</script>

<template>
  <q-page class="page-detail">
    <top-page-navigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      :preview-disabled-reason="previewDisabledReason"
      to="/pages"
      @preview="preview"
    />
    <div class="title">
      <div>
        <q-icon name="o_article" />
        <h1>{{ getTranslation(page, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div v-if="page">
      <dynamic-tabs :tabs="tabs" :active="openedTab" keep-alive>
        <template #infos>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="saveMeta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
          />
          <localized-input
            name="title"
            :label="t('page.title')"
            :editLocale="editLocale"
            outlined
            :language_enabled="languageEnabled"
          />
          <standard-input
            name="slug"
            :label="t('page.slug')"
            :hint="page.slug ? undefined : t('page.slug-optional')"
            outlined
          />
          <localized-select
            name="status"
            :label="t('page.status.label')"
            :options="statusOptions"
            emit-value
            map-options
            outlined
          />
        </template>
        <template #blocks>
          <module-list :parent="blockParent" :block-route-for="blockRouteFor" :languageEnabled="languageEnabled" />
        </template>
        <template #options>
          <form-actions
            :show-language-switcher="false"
            v-model:editLocale="editLocale"
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="saveMeta"
            :languageEnabled="languageEnabled"
          />
          <q-card class="q-mb-md">
            <q-card-section>
              <q-item-label header class="text-h6">{{ t('page.general') }}</q-item-label>
            </q-card-section>
            <q-separator />
            <q-card-section class="q-gutter-md">
              <q-input
                v-if="isThemeFieldLocked"
                :model-value="lockedThemeLabel"
                :label="t('page.theme.label')"
                :hint="t('page.theme.locked')"
                outlined
                readonly
                disable
              />
              <localized-select
                v-else
                name="theme_key"
                :label="t('page.theme.label')"
                :options="themeOptions"
                emit-value
                map-options
                outlined
                clearable
                :hint="themeHint"
                @update:model-value="onThemePicked"
              />
              <localized-select
                v-if="layoutChoices.length"
                name="layout_key"
                :label="t('page.layout.label')"
                :options="layoutChoices"
                emit-value
                map-options
                outlined
                @update:model-value="onLayoutPicked"
              />
            </q-card-section>
          </q-card>
          <q-card class="q-mb-md">
            <q-card-section>
              <q-item-label header class="text-h6">{{ t('page.languages') }}</q-item-label>
            </q-card-section>
            <q-separator />
            <q-card-section>
              <language-selector
                name="language_enabled"
                :label="t('page.language_enabled')"
                :language-enabled="community?.language_enabled"
              />
            </q-card-section>
          </q-card>
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
<style lang="scss">
.page-detail {
  padding: 16px;

  .title {
    margin: 20px 0;
    display: flex;
    flex-direction: row;
    padding: 0 10px;

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }
}
</style>
